/**
 * Real Dashboard home (Phase 2 of the admin redesign). Pulls from every
 * backend endpoint that actually exists today:
 *   - /api/analytics/summary  -> sessions/events trend (proxy for engagement;
 *     there is no true DAU/WAU/MAU/signups pipeline yet, so those tiles show "—")
 *   - /api/tag-review         -> pending AI tag review count
 *   - /api/admin/errors       -> open error count
 *   - /api/admin/audit-log    -> recent activity feed
 *   - /api/admin/config/genesys -> live config snapshot
 * No chart library is installed, so the trend is a hand-rolled inline SVG
 * bar chart rather than pulling in a new dependency.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Users as UsersIcon,
  Inbox,
  AlertOctagon,
  Sparkles,
  ScrollText,
  ArrowRight,
} from "lucide-react";
import PageHeader from "../components/ui/PageHeader";
import MetricCard from "../components/ui/MetricCard";
import StatusBadge from "../components/ui/StatusBadge";
import EmptyState from "../components/ui/EmptyState";
import { SkeletonCard, SkeletonRow } from "../components/ui/Skeleton";
import {
  apiAdminAnalyticsSummary,
  apiListTagReviewQueue,
  apiListErrors,
  apiListAuditLog,
  apiAdminGetGenesysConfig,
} from "../lib/cloudSync";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [pendingReviews, setPendingReviews] = useState(null);
  const [openErrors, setOpenErrors] = useState(null);
  const [investigatingErrors, setInvestigatingErrors] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [liveConfig, setLiveConfig] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError("");
      const results = await Promise.allSettled([
        apiAdminAnalyticsSummary(30),
        apiListTagReviewQueue("pending"),
        apiListErrors("open"),
        apiListErrors("investigating"),
        apiListAuditLog(undefined, 8),
        apiAdminGetGenesysConfig(),
      ]);
      const [a, reviews, errOpen, errInv, audit, config] = results;
      if (a.status === "fulfilled") setAnalytics(a.value);
      if (reviews.status === "fulfilled") setPendingReviews(reviews.value?.items?.length ?? 0);
      if (errOpen.status === "fulfilled") setOpenErrors(errOpen.value?.items?.length ?? 0);
      if (errInv.status === "fulfilled") setInvestigatingErrors(errInv.value?.items?.length ?? 0);
      if (audit.status === "fulfilled") setRecentActivity(audit.value?.items || []);
      if (config.status === "fulfilled") setLiveConfig(config.value?.config || null);
      if (results.every((r) => r.status === "rejected")) {
        setLoadError("Couldn't reach the backend. Check your connection and try refreshing.");
      }
      setLoading(false);
    })();
  }, []);

  const totals = analytics?.totals;
  const byDay = analytics?.by_day || [];
  const maxEvents = Math.max(1, ...byDay.map((d) => d.events || 0));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Operational overview across Deck Ratio's mobile, web, and backend."
        actions={
          <>
            <Link to="/admin/genesys" className="btn-celestial rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Push GENESYS update
            </Link>
            <Link
              to="/admin/tag-review"
              className="rounded-lg px-3 py-1.5 text-xs font-medium border border-celestial-border/30 text-celestial-muted hover:text-celestial-text flex items-center gap-1.5"
            >
              <Inbox className="w-3.5 h-3.5" /> Review queue
            </Link>
          </>
        }
      />

      {loadError && (
        <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{loadError}</div>
      )}

      {/* Top metric row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <MetricCard
              label="Sessions (30d)"
              value={totals?.unique_sessions ?? null}
              helper={totals ? `${totals.events} events` : "Analytics unavailable"}
              icon={Activity}
            />
            <MetricCard
              label="Unique users (30d)"
              value={totals?.unique_users ?? null}
              helper="proxy for DAU/WAU/MAU — not tracked yet"
              icon={UsersIcon}
            />
            <MetricCard
              label="AI Tag Reviews Pending"
              value={pendingReviews}
              helper="needsAdminReview queue"
              icon={Inbox}
              tone={pendingReviews > 0 ? "warning" : "default"}
            />
            <MetricCard
              label="Open Errors"
              value={openErrors}
              helper={investigatingErrors != null ? `${investigatingErrors} investigating` : undefined}
              icon={AlertOctagon}
              tone={openErrors > 0 ? "danger" : "default"}
            />
            <MetricCard
              label="Last Config Push"
              value={liveConfig?.version ?? "—"}
              helper={liveConfig?.updated_by ? `by ${liveConfig.updated_by}` : "no override pushed"}
              icon={Sparkles}
            />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Engagement trend */}
        <div className="card-celestial rounded-2xl p-5 lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-celestial-text">Engagement trend (30d)</h2>
            <span className="text-xs text-celestial-muted">events / day</span>
          </div>
          {loading ? (
            <div className="h-40 rounded-lg bg-surface/40 animate-pulse" />
          ) : byDay.length === 0 ? (
            <EmptyState
              icon={Activity}
              title="No analytics events yet."
              description="This chart fills in once analytics_events starts receiving traffic from mobile/web."
            />
          ) : (
            <svg viewBox={`0 0 ${byDay.length * 24} 120`} className="w-full h-32" preserveAspectRatio="none">
              {byDay.map((d, i) => {
                const h = Math.max(2, (d.events / maxEvents) * 100);
                return (
                  <rect
                    key={d.date || i}
                    x={i * 24 + 4}
                    y={120 - h}
                    width={14}
                    height={h}
                    rx={2}
                    className="fill-glow-mid/60"
                  >
                    <title>{`${d.date}: ${d.events} events, ${d.sessions} sessions`}</title>
                  </rect>
                );
              })}
            </svg>
          )}
        </div>

        {/* App health */}
        <div className="card-celestial rounded-2xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-celestial-text">App health</h2>
          {loading ? (
            <div className="space-y-2">
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-celestial-muted">Open errors</span>
                <StatusBadge status={openErrors > 0 ? "open" : "resolved"}>
                  {openErrors ?? "—"}
                </StatusBadge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-celestial-muted">Investigating</span>
                <StatusBadge status="investigating">{investigatingErrors ?? "—"}</StatusBadge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-celestial-muted">Crash-free rate</span>
                <span className="text-celestial-muted/60 text-xs">— (no crash reporting yet)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-celestial-muted">API uptime</span>
                <span className="text-celestial-muted/60 text-xs">— (no uptime check yet)</span>
              </div>
              <Link to="/admin/error-log" className="text-xs text-glow-light hover:text-glow-mid flex items-center gap-1 pt-1">
                View error log <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Recent activity */}
        <div className="card-celestial rounded-2xl lg:col-span-2">
          <div className="p-5 pb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-celestial-text">Recent activity</h2>
            <Link to="/admin/audit-log" className="text-xs text-glow-light hover:text-glow-mid flex items-center gap-1">
              View audit log <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {loading ? (
            <div className="divide-y divide-celestial-border/10">
              <SkeletonRow /><SkeletonRow /><SkeletonRow />
            </div>
          ) : recentActivity.length === 0 ? (
            <EmptyState icon={ScrollText} title="No admin activity yet." description="Pushes, resolutions, and config changes will show up here." />
          ) : (
            <div className="divide-y divide-celestial-border/10">
              {recentActivity.map((item) => (
                <div key={item.id} className="px-5 py-3 flex items-center justify-between gap-4 text-sm">
                  <div className="min-w-0">
                    <span className="text-celestial-text font-medium">{item.action}</span>{" "}
                    <span className="text-celestial-muted/70 text-xs">{item.section}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-xs text-celestial-muted">
                    <span>{item.admin_email || "unknown"}</span>
                    <span className="text-celestial-muted/60">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin action center */}
        <div className="card-celestial rounded-2xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-celestial-text">Admin action center</h2>
          <div className="space-y-2">
            <Link
              to="/admin/tag-review"
              className="flex items-center justify-between p-3 rounded-xl bg-surface/40 hover:bg-surface/70 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm text-celestial-text">
                <Inbox className="w-4 h-4 text-celestial-muted" /> Tag reviews to clear
              </span>
              <span className="text-sm font-semibold text-celestial-text">{loading ? "…" : pendingReviews ?? "—"}</span>
            </Link>
            <Link
              to="/admin/error-log"
              className="flex items-center justify-between p-3 rounded-xl bg-surface/40 hover:bg-surface/70 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm text-celestial-text">
                <AlertOctagon className="w-4 h-4 text-celestial-muted" /> Open errors to triage
              </span>
              <span className="text-sm font-semibold text-celestial-text">{loading ? "…" : openErrors ?? "—"}</span>
            </Link>
            <Link
              to="/admin/genesys"
              className="flex items-center justify-between p-3 rounded-xl bg-surface/40 hover:bg-surface/70 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm text-celestial-text">
                <Sparkles className="w-4 h-4 text-celestial-muted" /> GENESYS config
              </span>
              <span className="text-xs text-celestial-muted">{liveConfig?.version || "bundled default"}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
